import React, { useContext, useEffect, useState } from "react";
import Integrations from "../../../../constants/integrations";
import ExtensionCard from "../../Components/ExtensionCard";
import StepNavigation from "../../Components/StepNavigation";
import Api from "../../../../services/Api/Api";
import toast from "react-hot-toast";
import Context from "../../../../context/Global";
import LogTradeStepOne from "./Steps/LogTradeStepOne";
import { startPolling } from "../../utils/polling";
import LogTradeStepTwo from "./Steps/LogTradeStepTwo";

function CreateLogTradeExtension({ history }) {
    const context = useContext(Context);
    const [currentStep, setCurrentStep] = useState(1);
    const [activationProgress, setActivationProgress] = useState(false);
    const [prerequisitesChecked, setPrerequisitesChecked] = useState(false);

    const [configuration, setConfiguration] = useState({
        type: "LogTrade",
        identifier: "",
        state: "",
        extensionId: "",
        accessToken: "",
        companyName: "",
    });

    const initiateLoginProgress = async () => {
        setActivationProgress(true);
        try {
            const response = await Api.initiateLogTradeLoginProgress({
                customerId: context.user.customer.id,
            });
            if (response?.data?.token) {
                openLoginWindow({ state: response.data.token });
            } else {
                throw new Error("Failed to retrieve login state from backend.");
            }
        } catch (error) {
            toast.error("Misslyckades med att starta inloggningsprocessen.");
            setActivationProgress(false);
        }
    };

    const openLoginWindow = ({ state }) => {
        const zendrOrganisationId = `${process.env.REACT_APP_LOGTRADE_BASE_URL}/${process.env.REACT_APP_LOGTRADE_ORGANISATION_ID}`;

        const windowRef = window.open(
            `${process.env.REACT_APP_LOGTRADE_BASE_URL}/oauth/connect?client_id=${process.env.REACT_APP_LOGTRADE_CLIENT_ID}&state=${state}&connection_name=${configuration.companyName}&share_with_id=${zendrOrganisationId}&requesting_party=${zendrOrganisationId}`,
            "Connect to LogTrade",
            "width=1000,height=800",
        );

        if (!windowRef) {
            toast.error(
                "Misslyckades med att öppna LogTrade-inloggningsfönstret.",
            );
            setActivationProgress(false);
            return;
        }

        startPolling({
            pollingFunction: () => isExtensionCreated({ state }),
            onSuccess: () => {
                toast.success("Integrationen skapades framgångsrikt!");
                windowRef.close();
                setActivationProgress(false);
            },
            onTimeout: () => {
                toast.error("Tidsgränsen för inloggning har överskridits.", {
                    duration: 10000,
                });
                windowRef.close();
                setActivationProgress(false);
            },
            onError: () => {
                toast.error("Ett oväntat fel uppstod uppstod");
                windowRef.close();
                setActivationProgress(false);
            },
            onWindowClose: () => {
                toast.error("Inloggningsfönstret stängdes av användaren.");
                setActivationProgress(false);
            },
            windowRef,
        });
    };

    const isExtensionCreated = async ({ state }) => {
        try {
            const response = await Api.getCustomerIntegrationFromState({
                customerId: context.user.customer.id,
                stateId: state,
            });

            if (!response?.id) {
                return false;
            }

            setConfiguration((prevConf) => ({
                ...prevConf,
                extensionId: response.id,
            }));

            return true;
        } catch (error) {
            toast.error("Fel uppstod vid hämtning av integration");
            return false;
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setConfiguration((prevConf) => ({
            ...prevConf,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            history.push(`/extensions/${configuration.extensionId}`);
        } catch (error) {
            toast.error(
                "Misslyckades med att skapa integrationen, kontrollera uppgifterna och försök igen!",
            );
        }
    };

    const steps = [
        {
            component: (
                <LogTradeStepOne
                    prerequisitesChecked={prerequisitesChecked}
                    setPrerequisitesChecked={setPrerequisitesChecked}
                />
            ),
            isValid: () => prerequisitesChecked === true,
        },
        {
            component: (
                <LogTradeStepTwo
                    configuration={configuration}
                    handleChange={handleChange}
                    initiateLoginProgress={initiateLoginProgress}
                    activationProgress={activationProgress}
                />
            ),
            isValid: () => configuration.extensionId !== "",
        },
    ];

    const currentStepComponent = steps[currentStep - 1].component;

    return (
        <div className="min-h-full flex flex-col justify-between ml-20 pt-10">
            <div>
                <ExtensionCard extension={{ type: Integrations.LOG_TRADE }}>
                    <h1 className="text-2xl font-bold">Lägg till LogTrade</h1>
                    <p className="text-base font-medium mt-2">
                        För att kunna aktivera denna integration behöver du
                        följa stegen som beskrivs nedan.
                    </p>
                </ExtensionCard>
            </div>

            <div className="flex-grow flex">
                <div className="max-w w-full pt-10">{currentStepComponent}</div>
            </div>

            <div className="pb-12">
                <StepNavigation
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    onStepChanged={setCurrentStep}
                    isCurrentStepValid={steps[currentStep - 1].isValid()}
                    onSubmit={handleSubmit}
                />
            </div>
        </div>
    );
}

export default CreateLogTradeExtension;
